// Page callout — the one-sentence "what is this screen for" box above each
// page's content. Fed by the SAME catalog step the welcome panel walks, so the
// two surfaces can never describe a page differently.
//
// Mounted once in the shell rather than per route: the shell already knows the
// pathname, and per-route mounting would mean 40 opt-ins and 40 chances to
// forget one. A path with no catalog step (a detail route with no menu entry
// of its own, an unreachable page) simply renders nothing.

import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { cn } from "@/lib/utils";

import { useResolvableScreenAnchors } from "./screen-tour";
import { useTour } from "./tour-provider";

export function PageCallout(): React.ReactElement | null {
  const { t } = useTranslation();
  const tour = useTour();
  const { pathname } = useLocation();

  // Kancalar erken dönüşten ÖNCE çağrılmak zorunda; step henüz bilinmediği için
  // çapa çözümlemesi aşağıda, step'i bulduktan sonra tekrar ediliyor olamaz.
  // Bu yüzden pathname'den step'i burada çözüyoruz.
  const step = tour?.stepForPath(pathname);
  const anchors = useResolvableScreenAnchors(step?.key ?? null);

  if (tour === null) return null;
  // Bu kutu, kabuk turu açıkken GİZLENİYORDU. Gerekçesi gerçekti ve artık
  // geçersiz: eski panel adımının gövdesi ile bu kutu AYNI `tour.page.*`
  // cümlesini basıyordu, dolayısıyla ilk açılışta aynı cümle iki kez —
  // biri modalın içinde, biri arkasındaki sayfada — görünüyordu. Spotlight
  // `tour.shell.*` basıyor; tekrar yok.
  //
  // Ve satırı bırakmak SESSİZ bir kusur olurdu: kabuk turunun 5. çapası
  // (`shell-screen-tour`) tam da bu kutunun içinde yaşıyor, dolayısıyla kutu
  // gizliyken o adım hiç çözülemez ve "çözülemeyen adımı atla" kuralı beş
  // adımlık turu sessizce dörde indirirdi.
  if (step === undefined) return null;
  if (tour.isCalloutDismissed(step.key)) return null;

  return (
    <div
      className={cn(
        "mb-5 flex items-start gap-3 rounded-[10px] border border-[var(--border-subtle)]",
        "bg-[var(--bg-subtle)] px-4 py-3",
      )}
    >
      <div className="flex-1">
        <p className="font-sans text-sm text-[var(--fg-secondary)]">{t(step.bodyKey)}</p>
        {/* Ekran turunun tetikleyicisi. Bu kutu <main> içinde <Outlet/>'in
            hemen üstünde TEK noktada mount ediliyor, dolayısıyla 40 ekran tek
            dokunuşla tetikleyici kazanıyor — PageHeader'a gitmek 50 çağıranı
            değiştirmek olurdu.

            anchors.length KOŞULU ZORUNLU: her zaman boş açılan bir düğme, hiç
            düğme olmamasından kötüdür, çünkü kullanıcıya ürünün bozuk olduğunu
            öğretir. Manifest'te karşılığı olmayan ekranda da, çapaları DOM'da
            bulunmayan ekranda da düğme çizilmez. */}
        {anchors.length > 0 && (
          <button
            type="button"
            data-tour="shell-screen-tour"
            onClick={(): void => {
              tour.startScreenTour(step.key);
            }}
            className={cn(
              "mt-2 font-sans text-[13px] font-medium text-[var(--accent)] underline",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
            )}
          >
            {t("tour.card.introduce_screen")}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={(): void => {
          tour.dismissCallout(step.key);
        }}
        aria-label={t("tour.card.dismiss")}
        className={cn(
          "-mr-1 -mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px]",
          "text-[var(--fg-muted)] hover:bg-[var(--bg-raised)] hover:text-[var(--fg-primary)]",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
        )}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}
